import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Trash2, Plus } from "lucide-react";
import { useAdminData } from "@/hooks/useAdminData";
import { FEATURE_FLAGS, FEATURE_KEYS, FeatureKey, FeatureFlagRow } from "@/lib/featureFlags";

const flagsTable = () => (supabase as any).from("feature_flags");

export const FeatureFlagsTab = () => {
	const { toast } = useToast();
	const { users, organizations, isLoading: adminLoading } = useAdminData();
	const [rows, setRows] = useState<FeatureFlagRow[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [busyKey, setBusyKey] = useState<string | null>(null);

	const [addOpen, setAddOpen] = useState(false);
	const [addUserId, setAddUserId] = useState<string>("");
	const [addFeatureKey, setAddFeatureKey] = useState<FeatureKey>(FEATURE_KEYS[0]);
	const [addEnabled, setAddEnabled] = useState<boolean>(true);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const { data, error } = await flagsTable().select("*");
			if (error) throw error;
			setRows((data ?? []) as FeatureFlagRow[]);
		} catch (err: any) {
			toast({ title: "Error", description: err.message || "Failed to load feature flags", variant: "destructive" });
		} finally {
			setLoading(false);
		}
	}, [toast]);

	useEffect(() => {
		load();
	}, [load]);

	const orgRowIndex = useMemo(() => {
		const map = new Map<string, FeatureFlagRow>();
		for (const r of rows)
			if (r.organization_id)
				map.set(`${r.feature_key}::${r.organization_id}`, r);
		return map;
	}, [rows]);

	const userRows = useMemo(() => rows.filter(r => r.user_id), [rows]);

	const getOrgFlagEnabled = (feature: FeatureKey, orgId: string): boolean => {
		const row = orgRowIndex.get(`${feature}::${orgId}`);
		return row ? row.enabled : FEATURE_FLAGS[feature].defaultEnabled;
	};

	const setOrgFlag = async (feature: FeatureKey, orgId: string, enabled: boolean) => {
		const key = `org::${feature}::${orgId}`;
		setBusyKey(key);
		try {
			const existing = orgRowIndex.get(`${feature}::${orgId}`);
			if (existing) {
				const { error } = await flagsTable()
					.update({ enabled, updated_at: new Date().toISOString() })
					.eq("id", existing.id);
				if (error) throw error;
			} else {
				const { error } = await flagsTable()
					.insert({ feature_key: feature, organization_id: orgId, user_id: null, enabled });
				if (error) throw error;
			}
			await load();
		} catch (err: any) {
			toast({ title: "Error", description: err.message || "Failed to update flag", variant: "destructive" });
		} finally {
			setBusyKey(null);
		}
	};

	const setUserFlag = async (rowId: string, enabled: boolean) => {
		const key = `userRow::${rowId}`;
		setBusyKey(key);
		try {
			const { error } = await flagsTable()
				.update({ enabled, updated_at: new Date().toISOString() })
				.eq("id", rowId);
			if (error) throw error;
			await load();
		} catch (err: any) {
			toast({ title: "Error", description: err.message || "Failed to update override", variant: "destructive" });
		} finally {
			setBusyKey(null);
		}
	};

	const deleteUserFlag = async (rowId: string) => {
		const key = `userRow::${rowId}`;
		setBusyKey(key);
		try {
			const { error } = await flagsTable().delete().eq("id", rowId);
			if (error) throw error;
			await load();
		} catch (err: any) {
			toast({ title: "Error", description: err.message || "Failed to delete override", variant: "destructive" });
		} finally {
			setBusyKey(null);
		}
	};

	const addUserOverride = async () => {
		if (!addUserId || !addFeatureKey) return;
		setBusyKey("addUserOverride");
		try {
			const existing = userRows.find(r => r.user_id === addUserId && r.feature_key === addFeatureKey);
			if (existing) {
				const { error } = await flagsTable()
					.update({ enabled: addEnabled, updated_at: new Date().toISOString() })
					.eq("id", existing.id);
				if (error) throw error;
			} else {
				const { error } = await flagsTable()
					.insert({ feature_key: addFeatureKey, user_id: addUserId, organization_id: null, enabled: addEnabled });
				if (error) throw error;
			}
			setAddOpen(false);
			setAddUserId("");
			setAddEnabled(true);
			await load();
		} catch (err: any) {
			toast({ title: "Error", description: err.message || "Failed to add override", variant: "destructive" });
		} finally {
			setBusyKey(null);
		}
	};

	const userLabel = (userId: string) => {
		const u = users.find(x => x.id === userId);
		if (!u) return userId;
		const name = [u.firstName, u.lastName].filter(Boolean).join(" ");
		return name ? `${name} (${u.email})` : u.email;
	};

	const isBusy = (k: string) => busyKey === k;

	return (
		<div className="space-y-8">
			<div className="flex items-center justify-between">
				<div>
					<h2 className="text-xl font-semibold">Feature Flags</h2>
					<p className="text-sm text-muted-foreground">
						Enable features per organization or user. User overrides take precedence over organization settings.
					</p>
				</div>
				<Button onClick={load} variant="outline" size="sm" disabled={loading || adminLoading}>
					<RefreshCw className="mr-2 h-4 w-4" />
					Refresh
				</Button>
			</div>

			<section className="space-y-3">
				<div>
					<h3 className="text-base font-medium">Organizations</h3>
					<p className="text-xs text-muted-foreground">
						Toggle a feature per organization.
					</p>
				</div>
				<div className="border rounded-md">
					<Table>
						<TableHeader>
							<TableRow className="group">
								<TableHead className="min-w-[200px] sticky left-0 z-20 bg-background border-r">Organization</TableHead>
								{FEATURE_KEYS.map(key => (
									<TableHead key={key} className="min-w-[180px]">
										<div className="flex flex-col">
											<span>{FEATURE_FLAGS[key].label}</span>
											<span className="text-[10px] font-normal text-muted-foreground">{key}</span>
										</div>
									</TableHead>
								))}
							</TableRow>
						</TableHeader>
						<TableBody>
							{(loading || adminLoading) ? (
								<TableRow>
									<TableCell colSpan={FEATURE_KEYS.length + 1} className="text-center py-6 text-muted-foreground">
										Loading…
									</TableCell>
								</TableRow>
							) : organizations.length === 0 ? (
								<TableRow>
									<TableCell colSpan={FEATURE_KEYS.length + 1} className="text-center py-6 text-muted-foreground">
										No organizations
									</TableCell>
								</TableRow>
							) : organizations.map(org => (
								<TableRow key={org.id} className="group">
									<TableCell className="font-medium sticky left-0 z-10 bg-background group-hover:bg-muted border-r">{org.name}</TableCell>
									{FEATURE_KEYS.map(feature => {
										const busyK = `org::${feature}::${org.id}`;
										return (
											<TableCell key={feature}>
												<Switch
													checked={getOrgFlagEnabled(feature, org.id)}
													disabled={isBusy(busyK)}
													onCheckedChange={(v) => setOrgFlag(feature, org.id, v)}
												/>
											</TableCell>
										);
									})}
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			</section>

			<section className="space-y-3">
				<div className="flex items-center justify-between">
					<div>
						<h3 className="text-base font-medium">User overrides</h3>
						<p className="text-xs text-muted-foreground">
							Force a feature on or off for a specific user, regardless of their organization setting.
						</p>
					</div>
					<Button size="sm" onClick={() => setAddOpen(v => !v)}>
						<Plus className="mr-2 h-4 w-4" />
						{addOpen ? "Cancel" : "Add override"}
					</Button>
				</div>

				{addOpen && (
					<div className="border rounded-md p-4 flex flex-wrap items-end gap-4">
						<div className="flex flex-col gap-1 min-w-[240px]">
							<label className="text-xs font-medium">User</label>
							<Select value={addUserId} onValueChange={setAddUserId}>
								<SelectTrigger>
									<SelectValue placeholder="Select a user" />
								</SelectTrigger>
								<SelectContent>
									{users.map(u => (
										<SelectItem key={u.id} value={u.id}>{userLabel(u.id)}</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="flex flex-col gap-1 min-w-[200px]">
							<label className="text-xs font-medium">Feature</label>
							<Select value={addFeatureKey} onValueChange={(v) => setAddFeatureKey(v as FeatureKey)}>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{FEATURE_KEYS.map(k => (
										<SelectItem key={k} value={k}>{FEATURE_FLAGS[k].label}</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="flex items-center gap-2">
							<Switch checked={addEnabled} onCheckedChange={setAddEnabled} />
							<span className="text-sm">{addEnabled ? "Enabled" : "Disabled"}</span>
						</div>
						<Button onClick={addUserOverride} disabled={!addUserId || isBusy("addUserOverride")}>
							Save
						</Button>
					</div>
				)}

				<div className="border rounded-md overflow-x-auto">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>User</TableHead>
								<TableHead>Feature</TableHead>
								<TableHead>State</TableHead>
								<TableHead className="w-[80px]"></TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{loading ? (
								<TableRow>
									<TableCell colSpan={4} className="text-center py-6 text-muted-foreground">Loading…</TableCell>
								</TableRow>
							) : userRows.length === 0 ? (
								<TableRow>
									<TableCell colSpan={4} className="text-center py-6 text-muted-foreground">No user overrides</TableCell>
								</TableRow>
							) : userRows.map(row => {
								const busyK = `userRow::${row.id}`;
								const isKnownFeature = (FEATURE_KEYS as string[]).includes(row.feature_key);
								return (
									<TableRow key={row.id}>
										<TableCell>{row.user_id ? userLabel(row.user_id) : "—"}</TableCell>
										<TableCell>
											{isKnownFeature ? FEATURE_FLAGS[row.feature_key as FeatureKey].label : row.feature_key}
											{!isKnownFeature && <Badge variant="outline" className="ml-2 text-[10px]">unknown</Badge>}
										</TableCell>
										<TableCell>
											<div className="flex items-center gap-2">
												<Switch
													checked={row.enabled}
													disabled={isBusy(busyK)}
													onCheckedChange={(v) => setUserFlag(row.id, v)}
												/>
												<span className="text-sm text-muted-foreground">{row.enabled ? "Enabled" : "Disabled"}</span>
											</div>
										</TableCell>
										<TableCell>
											<Button
												variant="ghost"
												size="icon"
												className="h-8 w-8"
												disabled={isBusy(busyK)}
												onClick={() => deleteUserFlag(row.id)}
												title="Remove override"
											>
												<Trash2 className="h-4 w-4" />
											</Button>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</div>
			</section>
		</div>
	);
};
